"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { alphabetItems } from '@/app/a-to-z-of-branding/data';

interface AlphabetGridProps {
  highlightedLetter?: string | null;
}

const AlphabetGrid = ({ highlightedLetter }: AlphabetGridProps) => {
  const [columns, setColumns] = useState(11);
  const [clickedLetter, setClickedLetter] = useState<string | null>(
    highlightedLetter || null
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setIsMobile(true);
        setColumns(3); // mobile: 3 columns
      } else if (width < 1024) {
        setIsMobile(false);
        setColumns(7);
      } else {
        setIsMobile(false);
        setColumns(11);
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  useEffect(() => {
    if (highlightedLetter) {
       
      setClickedLetter(highlightedLetter);
    }
  }, [highlightedLetter]);

  if (isMobile) {
    // ✅ Mobile layout: 3 column grid, no border cells
    return (
      <section>
        <div className="grid grid-cols-3 ">
          {alphabetItems.map(({ letter, label, slug }) => (
            <Link
              key={letter}
              href={`/a-to-z-of-branding/${slug}`}
              aria-label={`${letter} - ${label}`}
              className={`flex flex-col card-hover-bg justify-between items-center text-center border border-[#C5C5C5]/20 bg-white hover:shadow-lg transition-all duration-300 p-4 min-h-[120px] cursor-pointer group ${
                clickedLetter === letter ? "card-hover-bg-active" : ""
              }`}
              onClick={() => {
                setClickedLetter(letter);
                localStorage.setItem("clickedLetter", letter);
              }}
            >
              <span
                className={`font-bold text-4xl leading-none transition-colors duration-300 ${
                  clickedLetter === letter
                    ? "text-primary"
                    : "text-gray-300 group-hover:text-gray-800"
                }`}
              >
                {letter}
              </span>
              <p className="mt-1 text-xs text-[#272d57] leading-snug">{label}</p>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  // ✅ Desktop/tablet layout: keep original bordered grid with empty border cells
  const cardsPerRow = columns - 2;
  const rows: typeof alphabetItems[] = [];
  for (let i = 0; i < alphabetItems.length; i += cardsPerRow) {
    rows.push(alphabetItems.slice(i, i + cardsPerRow));
  }

  return (
    <section>
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={`grid ${
            columns === 11
              ? "grid-cols-11"
              : columns === 7
              ? "grid-cols-7"
              : "grid-cols-5"
          }`}
        >
          {/* First empty border cell */}
          <div className="border-b border-[#C5C5C5]/20 h-full w-full" />

          {/* Content cards */}
          {row.map(({ letter, label, slug }) => (
            <Link
              key={letter}
              href={`/a-to-z-of-branding/${slug}`}
              aria-label={`${letter} - ${label}`}
              className={`flex flex-col card-hover-bg justify-between items-center text-center border border-[#C5C5C5]/20 bg-white hover:shadow-lg transition-all duration-300 p-4 sm:p-5 md:p-6 min-h-[130px] sm:min-h-[150px] md:min-h-[180px] cursor-pointer group ${
                clickedLetter === letter ? "card-hover-bg-active" : ""
              }`}
              onClick={() => {
                setClickedLetter(letter);
                localStorage.setItem("clickedLetter", letter);
              }}
            >
              <span
                className={`font-bold text-3xl sm:text-4xl md:text-7xl leading-none transition-colors duration-300 ${
                  clickedLetter === letter
                    ? "text-primary"
                    : "text-gray-300 group-hover:text-gray-800"
                }`}
              >
                {letter}
              </span>
              <p className="mt-2 text-sm text-[#272d57] leading-snug">{label}</p>
            </Link>
          ))}

          {/* Fill blank cells if last row is short */}
          {Array.from({ length: cardsPerRow - row.length }).map((_, i) => (
            <div key={`empty-${i}`} className="border border-[#C5C5C5]/20" />
          ))}

          {/* Last empty border cell */}
          <div className="border border-[#C5C5C5]/20 h-full w-full" />
        </div>
      ))}
    </section>
  );
};

export default AlphabetGrid;
